import { useState, useEffect } from 'react';

export default function MultiRadio({
  options = [], // [{ value: 'a', label: 'Option A' }, ...] (value can now be an object)
  showLabels = true,
  isTable = false,
  selected, // controlled value (optional) - can be object
  onChange = () => {}, // called with new value (object)
  name,
  layout = 'vertical', // 'vertical' | 'horizontal'
  required = false,
  className = '',
}) {
  const [internal, setInternal] = useState(() => {
    if (selected !== undefined) return selected;
    return required ? null : (options?.[0]?.value ?? null);
  });

  // keep internal state in sync if controlled prop or options change
  useEffect(() => {
    if (selected !== undefined) {
      setInternal(selected);
      return;
    }
    // when options change and not required, default to first; if required keep null
    setInternal(required ? null : (options?.[0]?.value ?? null));
  }, [options, selected, required]);

  const current = selected !== undefined ? selected : internal;

  const handleChange = (val) => {
    if (selected === undefined) setInternal(val);
    onChange(val);
  };

  const equal = (a, b) => {
    try {
      return JSON.stringify(a) === JSON.stringify(b);
    } catch {
      return a === b;
    }
  };

  return (
    <fieldset
      className={`${isTable ? 'w-full justify-around' : ''}  flex ${
        layout === 'horizontal' ? 'flex-row gap-4' : 'flex-col gap-2'
      } ${className}`}
      aria-label={name}
    >
      {options.map((opt, idx) => {
        const id = `${name}-${String(opt.label)}-${String(
          typeof opt.value === 'object' ? JSON.stringify(opt.value) : opt.value,
        )}`;
        const checked = equal(current, opt.value);
        return (
          <label
            key={id}
            htmlFor={id}
            className="flex items-center gap-2 cursor-pointer select-none text-light dark:text-dark"
          >
            <input
              id={id}
              name={name}
              type="radio"
              // input.value must be a string for the DOM; serialize objects
              value={typeof opt.value === 'object' ? JSON.stringify(opt.value) : opt.value}
              checked={checked}
              onChange={() => handleChange(opt.value)}
              required={required}
              className={`flex-none m-0 cursor-pointer rounded-xl border-2 ${
                checked
                  ? 'border-blue bg-blue shadow-[inset_0_0_0_4px_white] dark:shadow-[inset_0_0_0_4px_#38363E]'
                  : 'border-gray bg-transparent shadow-none'
              }`}
              style={{
                width: 20,
                height: 20,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
              }}
            />
            {showLabels && <span className="text-sm">{opt.label}</span>}
          </label>
        );
      })}
    </fieldset>
  );
}
